/**
 * space_map.ts — the SYSTEM CHART (M in space): an orbit-camera axonometric
 * chart of the star system you are IN — its star, orbit rings, shaded
 * planets with atmosphere rims, asteroid belts as speckled rings, nebulae,
 * stations, pirate dens, charted user worlds, your ship as a heading
 * chevron with its nav lane, and the live battle marker. Mouse-driven
 * (chart_view): wheel zooms on the cursor, drag orbits, right-drag pans,
 * double-click focuses; hover reads a marker; click a POI to AUTOPILOT.
 *
 * Pure presentation like space_hud: SpaceModule feeds update() a snapshot
 * of THE CURRENT SYSTEM (the Commons list or a derived alt-system sector —
 * the 2026-08-24 "local map must match the system" wave) and receives
 * clicks through the constructor callbacks.
 */
import { ChartView, niceStep, fmtKm, hash01 } from './chart_view.js';

export interface MapPoi { name: string; kind: string; pos: readonly number[]; dist: number; tier: number; radius?: number }
export interface MapPlanet { name: string; pos: readonly number[]; radius: number; hue?: number; rim?: number }
/** A PUBLIC user-created world hanging as a planet (the galactic layer). */
export interface MapWorld { name: string; owner: string; worldId: string; pos: readonly number[]; radius: number; hue: number; dist: number }
export interface MapData {
  systemName: string;
  /** The primary star at the origin (null in the Commons sector, where the Commons planet sits there). */
  star: { color: number; radius: number } | null;
  /** Ship forward vector (x, z). */
  heading: readonly number[];
  you: readonly number[];
  nav: string | null;
  pois: MapPoi[];
  planets: MapPlanet[];
  worlds: MapWorld[];
  /** The live faction battle (or the next one) — pinned + pulsing on the chart. */
  battle: { name: string; pos: readonly number[]; active: boolean; secs: number } | null;
}

const KIND_COLOR: Record<string, string> = {
  station: '#7ec8e3', megastation: '#aee6ff', asteroids: '#8a97a8', junkyard: '#c9a37e',
  nebula: '#b48ae3', ice: '#9fdbe8', derelict: '#c9a37e', comet: '#9fdbe8',
  mine: '#c9a37e', fortress: '#ff6e6e',
  // natural objects (2026-10-07): a system's own bodies (data identities, like the kinds above)
  n_comet: '#9fdbe8', n_moon: '#c8ccd4', n_dwarf: '#d9c2a8', n_asteroid: '#a89c8c', n_trojans: '#8a97a8', n_rogue: '#7d6f96', n_visitor: '#c98a7a', n_centaur: '#8f9fb0',
};
const KIND_GLYPH: Record<string, string> = {
  station: '◇', megastation: '◈', asteroids: '∴', junkyard: '☒', nebula: '≈',
  ice: '❄', derelict: '☠', comet: '☄', mine: '⛏', fortress: '⚑',
  n_comet: '☄', n_moon: '○', n_dwarf: '◍', n_asteroid: '◆', n_trojans: '∴', n_rogue: '◌', n_visitor: '➶', n_centaur: '◦',
};
const KIND_LABEL: Record<string, string> = {
  station: 'station', megastation: 'megastation', asteroids: 'asteroid belt', junkyard: 'junkyard',
  nebula: 'nebula', ice: 'ice field', derelict: 'derelict', comet: 'comet', mine: 'mining rig', fortress: 'pirate den',
  n_comet: 'comet', n_moon: 'moon', n_dwarf: 'dwarf planet', n_asteroid: 'asteroid', n_trojans: 'trojan swarm', n_rogue: 'rogue planet', n_visitor: 'interstellar visitor', n_centaur: 'centaur',
};
const RADAR_RING_M = 12_000;

interface Mark { kind: 'poi' | 'planet' | 'world'; id: string; sx: number; sy: number; depth: number; r: number; label: string; sub: string }

const hex = (c: number): string => `#${c.toString(16).padStart(6, '0')}`;

export class SpaceMap {
  private root: HTMLDivElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private listEl: HTMLDivElement | null = null;
  private titleEl: HTMLSpanElement | null = null;
  private subEl: HTMLSpanElement | null = null;
  private zoomEl: HTMLSpanElement | null = null;
  private data: MapData | null = null;
  private raf = 0;
  private readonly view = new ChartView(0.4, 80, 0.12);
  private marks: Mark[] = [];
  private hover: Mark | null = null;
  private filter = '';
  private dust: Array<[number, number, number]> = [];
  private readonly onSelect: (name: string) => void;
  private readonly onWorldWarp: (worldId: string) => void;
  private readonly onClose: (() => void) | null;
  private readonly onResize = (): void => this.layout();
  // Escape closes THIS chart and nothing else (capture phase beats every bubble-phase Esc menu).
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape' || !this.root) return;
    e.stopImmediatePropagation(); e.preventDefault();
    this.onClose?.();
  };

  constructor(onSelect: (name: string) => void, onWorldWarp: (worldId: string) => void, onClose: (() => void) | null = null) {
    this.onSelect = onSelect;
    this.onWorldWarp = onWorldWarp;
    this.onClose = onClose;
    for (let i = 0; i < 260; i++) this.dust.push([hash01(`dx${i}`), hash01(`dy${i}`), 0.25 + hash01(`da${i}`) * 0.6]);
  }

  mount(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'space-map';
    root.style.cssText =
      'position:fixed;inset:0;z-index:38;display:flex;align-items:center;justify-content:center;gap:18px;' +
      'background:color-mix(in srgb, var(--ax-bg) 85%, transparent);backdrop-filter:blur(3px);font:12px var(--ax-font);color:var(--ax-text);pointer-events:auto';
    const left = document.createElement('div');
    left.style.cssText = 'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:12px;position:relative';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:baseline;gap:10px;margin-bottom:8px';
    const title = document.createElement('span');
    title.textContent = 'SYSTEM CHART';
    title.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px';
    const name = document.createElement('span');
    name.style.cssText = 'font:700 14px var(--ax-font);color:var(--ax-text)';
    const sub = document.createElement('span');
    sub.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-muted);flex:1';
    const zoomLbl = document.createElement('span');
    zoomLbl.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-muted)';
    head.append(title, name, sub, zoomLbl);
    this.titleEl = name; this.subEl = sub; this.zoomEl = zoomLbl;

    const cv = document.createElement('canvas');
    cv.style.cssText = 'display:block;cursor:grab;border-radius:var(--ax-radius);background:radial-gradient(circle at 50% 45%,var(--ax-bg2) 0%,var(--ax-bg) 70%,var(--ax-bg) 100%)';
    this.canvas = cv;
    this.view.attach(cv, {
      onClick: (x, y) => { const m = this.pick(x, y); if (m) this.activate(m); },
      onDblClick: (x, y) => { const m = this.pick(x, y); if (m) this.view.focus(m.sx, m.sy, 1.9); else this.view.focus(x, y, 1.6); },
    });

    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;margin-top:8px;align-items:center';
    const mk = (label: string, tip: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label; b.title = tip;
      b.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 10px;cursor:pointer;font:12px var(--ax-font)';
      b.onclick = fn;
      return b;
    };
    const c = (): number => this.view.size / 2;
    bar.append(
      mk('−', 'Zoom out', () => this.view.zoomAt(c(), c(), 1 / 1.4)),
      mk('+', 'Zoom in', () => this.view.zoomAt(c(), c(), 1.4)),
      mk('◉ ship', 'Center on your ship', () => this.focusShip()),
      mk('⟲ orbit', 'Toggle the slow auto-orbit', () => { this.view.autoOrbit = !this.view.autoOrbit; }),
      mk('⌂ fit', 'Reset the view', () => this.view.reset()),
    );
    const legend = document.createElement('span');
    legend.style.cssText = 'margin-left:auto;font:10px var(--ax-mono);color:var(--ax-muted)';
    legend.textContent = 'drag — orbit · wheel — zoom · right-drag — pan · dbl-click — focus';
    bar.appendChild(legend);
    left.append(head, cv, bar);

    const right = document.createElement('div');
    right.style.cssText =
      'width:330px;max-height:82vh;display:flex;flex-direction:column;background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line);' +
      'border-radius:var(--ax-win-radius);padding:12px';
    const rTitle = document.createElement('div');
    rTitle.textContent = 'NAV TARGETS — click to AUTOPILOT';
    rTitle.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);letter-spacing:1px;margin-bottom:8px';
    const search = document.createElement('input');
    search.placeholder = 'filter…';
    search.style.cssText = 'width:100%;box-sizing:border-box;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:4px 8px;font:12px var(--ax-font);margin-bottom:8px';
    search.addEventListener('input', () => { this.filter = search.value.trim().toLowerCase(); this.rebuildList(); });
    search.addEventListener('keydown', (e) => e.stopPropagation());
    const list = document.createElement('div');
    list.style.cssText = 'overflow:auto;flex:1';
    right.append(rTitle, search, list);
    this.listEl = list;

    const hint = document.createElement('div');
    hint.textContent = 'M / Esc — close · G — GALACTIC chart · N cycles nav · J jumps';
    hint.style.cssText = 'position:absolute;bottom:18px;left:50%;transform:translateX(-50%);color:var(--ax-muted)';
    root.append(left, right, hint);
    document.body.appendChild(root);
    this.root = root;
    this.layout();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey, true);
    const draw = (): void => { this.draw(); this.raf = requestAnimationFrame(draw); };
    this.raf = requestAnimationFrame(draw);
  }

  unmount(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey, true);
    this.root?.remove();
    this.root = null;
    this.canvas = null;
    this.listEl = null;
  }

  update(data: MapData): void {
    const first = !this.data;
    this.data = data;
    if (this.titleEl) this.titleEl.textContent = data.systemName;
    if (this.subEl) this.subEl.textContent = `${data.planets.length} planets · ${data.pois.length} points of interest${data.worlds.length ? ` · ${data.worlds.length} charted worlds` : ''}`;
    this.rebuildList();
    if (first) this.view.reset();
  }

  private layout(): void {
    const cv = this.canvas;
    if (!cv) return;
    const size = Math.max(360, Math.min(920, window.innerHeight - 170, window.innerWidth - 460));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.view.size = size;
    cv.style.width = `${size}px`; cv.style.height = `${size}px`;
    cv.width = Math.round(size * dpr); cv.height = Math.round(size * dpr);
  }

  private focusShip(): void {
    const d = this.data;
    if (!d) return;
    const s = this.scale();
    const [sx, sy] = this.view.proj(d.you[0], d.you[1], d.you[2], s);
    this.view.focus(sx, sy, 1);
  }

  private activate(m: Mark): void {
    if (m.kind === 'world') this.onWorldWarp(m.id);
    else this.onSelect(m.id);
  }

  private pick(x: number, y: number): Mark | null {
    let best: Mark | null = null, bd = 14 * 14;
    for (const m of this.marks) {
      const dd = (m.sx - x) ** 2 + (m.sy - y) ** 2;
      const lim = Math.max(bd, (m.r + 6) ** 2);
      if (dd < lim && (best === null || dd < bd)) { bd = dd; best = m; }
    }
    return best;
  }

  private rebuildList(): void {
    const list = this.listEl;
    if (!list || !this.data) return;
    list.replaceChildren();
    const f = this.filter;
    const row = (glyph: string, color: string, name: string, meta: string, active: boolean, tint: string, fn: () => void, owner?: string): void => {
      const b = document.createElement('button');
      b.style.cssText =
        `display:flex;width:100%;gap:8px;align-items:center;text-align:left;cursor:pointer;margin-bottom:3px;background:${tint};` +
        `border:1px solid ${active ? 'var(--ax-good)' : 'var(--ax-line)'};border-radius:var(--ax-radius);padding:6px 10px;color:var(--ax-text);font:12px var(--ax-font)`;
      const g = document.createElement('span'); g.textContent = glyph; g.style.cssText = `width:16px;color:${color}`;
      const n = document.createElement('span'); n.textContent = name; n.style.cssText = 'flex:1;font-weight:600';
      const m = document.createElement('span'); m.textContent = meta; m.style.cssText = 'color:var(--ax-muted);font:11px var(--ax-mono)';
      b.append(g, n);
      if (owner) { const o = document.createElement('span'); o.textContent = owner; o.style.cssText = 'color:var(--ax-muted);font:11px var(--ax-font)'; b.appendChild(o); }
      b.appendChild(m);
      b.onclick = fn;
      list.appendChild(b);
    };
    const section = (text: string, color = 'var(--ax-muted)'): void => {
      const t = document.createElement('div');
      t.textContent = text;
      t.style.cssText = `font:700 10px var(--ax-mono);color:${color};letter-spacing:1px;margin:10px 0 6px`;
      list.appendChild(t);
    };
    const d = this.data;
    const pois = [...d.pois].filter((p) => !f || p.name.toLowerCase().includes(f)).sort((a, b) => a.dist - b.dist);
    for (const p of pois) {
      row(KIND_GLYPH[p.kind] ?? '·', KIND_COLOR[p.kind] ?? '#8a97a8', p.name, `${'☠'.repeat(p.tier)} ${fmtKm(p.dist)}`, d.nav === p.name, 'color-mix(in srgb, var(--ax-bg2) 70%, transparent)', () => this.onSelect(p.name));
    }
    const planets = d.planets.filter((p) => !f || p.name.toLowerCase().includes(f));
    if (planets.length) section('PLANETS — click to set nav', '#c9b98a');
    for (const p of planets) {
      const dist = Math.hypot(p.pos[0] - d.you[0], p.pos[1] - d.you[1], p.pos[2] - d.you[2]);
      row('●', this.planetColor(p), p.name, `${fmtKm(p.radius)} radius · ${fmtKm(dist)}`, d.nav === p.name, 'rgba(30,26,18,.6)', () => this.onSelect(p.name));
    }
    const worlds = [...d.worlds].filter((w) => !f || w.name.toLowerCase().includes(f) || w.owner.toLowerCase().includes(f)).sort((a, b) => a.dist - b.dist);
    section(worlds.length > 0 ? `CHARTED WORLDS (${worlds.length}) — click to HYPERWARP` : 'CHARTED WORLDS — none yet (public worlds hang here)', '#a98fd1');
    for (const w of worlds) {
      row('◉', `hsl(${Math.round(w.hue * 360)}, 55%, 70%)`, w.name, fmtKm(w.dist), false, 'rgba(22,16,42,.7)', () => this.onWorldWarp(w.worldId), w.owner);
    }
  }

  private planetColor(p: MapPlanet): string {
    if (p.rim !== undefined) return hex(p.rim);
    return `hsl(${Math.round((p.hue ?? 0.1) * 360)}, 45%, 68%)`;
  }

  /** px per meter at zoom 1: the farthest marker defines the fit. */
  private scale(): number {
    const d = this.data;
    const c = this.view.size / 2;
    let maxR = 100_000;
    if (d) {
      for (const p of [...d.pois.map((x) => x.pos), ...d.planets.map((x) => x.pos), ...d.worlds.map((x) => x.pos), d.you]) {
        maxR = Math.max(maxR, Math.hypot(p[0], p[2]) * 1.08, Math.abs(p[1]) * 2.4);
      }
    }
    return ((c - 30) / maxR) * this.view.zoom;
  }

  private ring(g: CanvasRenderingContext2D, r: number, y: number, s: number): void {
    g.beginPath();
    for (let i = 0; i <= 72; i++) {
      const t = (i / 72) * Math.PI * 2;
      const [sx, sy] = this.view.proj(Math.cos(t) * r, y, Math.sin(t) * r, s);
      if (i === 0) g.moveTo(sx, sy); else g.lineTo(sx, sy);
    }
    g.closePath();
  }

  private draw(): void {
    const cv = this.canvas;
    const g = cv?.getContext('2d');
    const d = this.data;
    if (!cv || !g || !d) return;
    const now = performance.now();
    this.view.tick(now);
    const S = this.view.size;
    const dpr = cv.width / S;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, S, S);
    const s = this.scale();
    const v = this.view;
    if (this.zoomEl) this.zoomEl.textContent = `zoom ×${v.zoom.toFixed(v.zoom < 3 ? 1 : 0)}`;

    // Dust: a static, faint speckle field for depth.
    g.fillStyle = '#cfe0f5';
    for (const [x, y, a] of this.dust) { g.globalAlpha = a * 0.16; g.fillRect(x * S, y * S, 1, 1); }
    g.globalAlpha = 1;

    // Ecliptic rings at nice distances + labels; spokes every 45°.
    const visibleR = (S / 2) / s;
    const step = niceStep(visibleR, 3);
    g.lineWidth = 1;
    for (let k = 1; k <= 6; k++) {
      const r = step * k;
      if (r * s > S * 1.6) break;
      g.strokeStyle = `rgba(36,64,94,${k === 1 ? 0.6 : 0.42})`;
      this.ring(g, r, 0, s);
      g.stroke();
      const [lx, ly] = v.proj(r, 0, 0, s);
      g.fillStyle = 'rgba(111,140,174,.7)';
      g.font = '10px ui-monospace,monospace';
      g.fillText(fmtKm(r), lx + 4, ly - 3);
    }
    g.strokeStyle = 'rgba(36,64,94,.28)';
    for (let i = 0; i < 8; i++) {
      const t = (i / 8) * Math.PI * 2;
      const rr = step * 6;
      const [ax, ay] = v.proj(0, 0, 0, s);
      const [bx, by] = v.proj(Math.cos(t) * rr, 0, Math.sin(t) * rr, s);
      g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
    }

    // The star at the origin (alt systems): layered glow + disc.
    if (d.star) {
      const [sx, sy] = v.proj(0, 0, 0, s);
      const col = hex(d.star.color);
      // PHYSICAL scale (a 7 px floor so a distant view still shows a star): the planet layout law
      // seats the first orbit at 3 star radii + 150 km, so a true disc no longer swallows any orbit.
      // Only the GLOW is capped — at a close zoom it would otherwise wash the whole chart.
      const rr = Math.max(7, d.star.radius * s);
      const gr = Math.min(rr * 2.6, rr + 90);
      const glow = g.createRadialGradient(sx, sy, rr * 0.4, sx, sy, gr);
      glow.addColorStop(0, col + 'aa'); glow.addColorStop(0.4, col + '33'); glow.addColorStop(1, col + '00');
      g.fillStyle = glow;
      g.beginPath(); g.arc(sx, sy, gr, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#fff8e8';
      g.beginPath(); g.arc(sx, sy, rr, 0, Math.PI * 2); g.fill();
      g.fillStyle = 'rgba(255,240,200,.75)';
      g.font = '10px ui-monospace,monospace';
      g.fillText(`R ${fmtKm(d.star.radius)}`, sx + rr + 5, sy + 4);
      // Orbit rings through every planet.
      g.strokeStyle = 'rgba(200,180,120,.32)';
      g.setLineDash([3, 5]);
      for (const p of d.planets) { this.ring(g, Math.hypot(p.pos[0], p.pos[2]), p.pos[1], s); g.stroke(); }
      g.setLineDash([]);
    }

    // Volumes: asteroid belts as speckled rings, nebulae as haze.
    for (const p of d.pois) {
      if (!p.radius) continue;
      if (p.kind === 'asteroids' || p.kind === 'ice' || p.kind === 'junkyard' || p.kind === 'mine') {
        const n = 34;
        g.fillStyle = KIND_COLOR[p.kind] ?? '#8a97a8';
        for (let i = 0; i < n; i++) {
          const t = hash01(`${p.name}a${i}`) * Math.PI * 2;
          const rr = p.radius * (0.25 + hash01(`${p.name}r${i}`) * 0.85);
          const [sx, sy] = v.proj(p.pos[0] + Math.cos(t) * rr, p.pos[1] + (hash01(`${p.name}y${i}`) - 0.5) * p.radius * 0.4, p.pos[2] + Math.sin(t) * rr, s);
          g.globalAlpha = 0.35 + hash01(`${p.name}o${i}`) * 0.4;
          g.fillRect(sx, sy, 1.5, 1.5);
        }
        g.globalAlpha = 1;
      } else if (p.kind === 'nebula') {
        const [sx, sy] = v.proj(p.pos[0], p.pos[1], p.pos[2], s);
        const rr = Math.max(16, p.radius * s);
        const hz = g.createRadialGradient(sx, sy, 0, sx, sy, rr);
        hz.addColorStop(0, 'rgba(180,138,227,.35)'); hz.addColorStop(1, 'rgba(180,138,227,0)');
        g.fillStyle = hz;
        g.beginPath(); g.arc(sx, sy, rr, 0, Math.PI * 2); g.fill();
      }
    }

    // Markers, depth-sorted (far first).
    const marks: Mark[] = [];
    const [ox, oy] = v.proj(0, 0, 0, s);
    for (const p of d.planets) {
      const [sx, sy, depth] = v.proj(p.pos[0], p.pos[1], p.pos[2], s);
      const r = Math.min(46, Math.max(4.5, Math.log10(p.radius) * 3.2 * Math.sqrt(v.zoom), p.radius * s));
      marks.push({ kind: 'planet', id: p.name, sx, sy, depth, r, label: p.name, sub: `planet · ${fmtKm(p.radius)} radius` });
    }
    for (const p of d.pois) {
      const [sx, sy, depth] = v.proj(p.pos[0], p.pos[1], p.pos[2], s);
      marks.push({ kind: 'poi', id: p.name, sx, sy, depth, r: p.kind === 'megastation' ? 7 : 5.5, label: p.name, sub: `${KIND_LABEL[p.kind] ?? p.kind}${p.tier ? ' · ' + '☠'.repeat(p.tier) : ''} · ${fmtKm(p.dist)}` });
    }
    for (const w of d.worlds) {
      const [sx, sy, depth] = v.proj(w.pos[0], w.pos[1], w.pos[2], s);
      marks.push({ kind: 'world', id: w.worldId, sx, sy, depth, r: Math.max(4.5, Math.log10(w.radius) * 2.6 * Math.sqrt(v.zoom)), label: w.name, sub: `charted world · ${w.owner} · ${fmtKm(w.dist)}` });
    }
    marks.sort((a, b) => a.depth - b.depth);
    const poiByName = new Map(d.pois.map((p) => [p.name, p]));
    const planetByName = new Map(d.planets.map((p) => [p.name, p]));
    const worldById = new Map(d.worlds.map((w) => [w.worldId, w]));
    for (const m of marks) {
      // altitude stem down to the ecliptic (the classic 3D-map readability trick)
      const src = m.kind === 'poi' ? poiByName.get(m.id)!.pos : m.kind === 'planet' ? planetByName.get(m.id)!.pos : worldById.get(m.id)!.pos;
      const [fx, fy] = v.proj(src[0], 0, src[2], s);
      g.strokeStyle = 'rgba(120,150,190,.28)';
      g.beginPath(); g.moveTo(m.sx, m.sy); g.lineTo(fx, fy); g.stroke();
      g.fillStyle = 'rgba(120,150,190,.35)';
      g.beginPath(); g.arc(fx, fy, 1.5, 0, Math.PI * 2); g.fill();
      const isNav = d.nav === m.id;
      const isHover = this.hover?.id === m.id && this.hover.kind === m.kind;
      if (m.kind === 'planet') {
        const p = planetByName.get(m.id)!;
        const base = this.planetColor(p);
        // Lit from the star (origin) — the gradient center leans toward it.
        const lx = ox - m.sx, ly = oy - m.sy;
        const ll = Math.hypot(lx, ly) || 1;
        const grad = g.createRadialGradient(m.sx + (lx / ll) * m.r * 0.45, m.sy + (ly / ll) * m.r * 0.45, m.r * 0.1, m.sx, m.sy, m.r);
        grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.25, base); grad.addColorStop(1, '#0a0f18');
        g.fillStyle = grad;
        g.beginPath(); g.arc(m.sx, m.sy, m.r, 0, Math.PI * 2); g.fill();
        g.strokeStyle = base; g.globalAlpha = 0.55;
        g.beginPath(); g.arc(m.sx, m.sy, m.r + 1.5, 0, Math.PI * 2); g.stroke();
        g.globalAlpha = 1;
      } else if (m.kind === 'world') {
        const w = worldById.get(m.id)!;
        const col = `hsl(${Math.round(w.hue * 360)}, 55%, 70%)`;
        const grad = g.createRadialGradient(m.sx - m.r * 0.3, m.sy - m.r * 0.3, m.r * 0.1, m.sx, m.sy, m.r);
        grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.3, col); grad.addColorStop(1, '#120a20');
        g.fillStyle = grad;
        g.beginPath(); g.arc(m.sx, m.sy, m.r, 0, Math.PI * 2); g.fill();
        g.strokeStyle = col;
        g.beginPath(); g.arc(m.sx, m.sy, m.r + 3, 0, Math.PI * 2); g.stroke();
      } else {
        const p = poiByName.get(m.id)!;
        const col = KIND_COLOR[p.kind] ?? '#8a97a8';
        g.fillStyle = col;
        g.font = `${p.kind === 'megastation' ? 15 : 12}px ui-monospace,monospace`;
        g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(KIND_GLYPH[p.kind] ?? '·', m.sx, m.sy);
        g.textAlign = 'start'; g.textBaseline = 'alphabetic';
      }
      if (isNav) {
        g.strokeStyle = '#9fe3c0'; g.lineWidth = 1.5;
        g.beginPath(); g.arc(m.sx, m.sy, m.r + 5 + Math.sin(now / 260) * 1.2, 0, Math.PI * 2); g.stroke();
        g.lineWidth = 1;
      }
      if (isHover) {
        g.strokeStyle = '#ffffff';
        g.beginPath(); g.arc(m.sx, m.sy, m.r + 7, 0, Math.PI * 2); g.stroke();
      }
      const showLabel = m.kind !== 'poi' || v.zoom >= 1.5 || isNav || isHover || d.pois.length <= 10;
      if (showLabel) {
        g.fillStyle = m.kind === 'planet' ? 'rgba(232,220,180,.85)' : m.kind === 'world' ? 'rgba(226,213,245,.85)' : 'rgba(207,224,245,.75)';
        g.font = `${m.kind === 'planet' ? '600 11px' : '10px'} ui-monospace,monospace`;
        g.fillText(m.label, m.sx + m.r + 4, m.sy + 3);
      }
    }
    this.marks = marks;

    // YOU: nav lane + heading chevron + sensor ring.
    const [yx, yy] = v.proj(d.you[0], d.you[1], d.you[2], s);
    if (d.nav) {
      const t = poiByName.get(d.nav)?.pos ?? planetByName.get(d.nav)?.pos;
      if (t) {
        const [tx, ty] = v.proj(t[0], t[1], t[2], s);
        g.strokeStyle = 'rgba(159,227,192,.75)';
        g.setLineDash([5, 5]);
        g.beginPath(); g.moveTo(yx, yy); g.lineTo(tx, ty); g.stroke();
        g.setLineDash([]);
        const dist = Math.hypot(t[0] - d.you[0], t[1] - d.you[1], t[2] - d.you[2]);
        g.fillStyle = '#9fe3c0';
        g.font = '10px ui-monospace,monospace';
        g.fillText(`${d.nav} · ${fmtKm(dist)}`, (yx + tx) / 2 + 6, (yy + ty) / 2 - 4);
      }
    }
    g.strokeStyle = 'rgba(188,215,255,.25)';
    this.ring(g, RADAR_RING_M, d.you[1], s);
    g.stroke();
    const [hx, hy] = v.dir(d.heading[0], 0, d.heading[1]);
    const pulse = 6 + Math.sin(now / 300) * 1.5;
    const px = -hy, py = hx;
    g.fillStyle = '#ffffff';
    g.beginPath();
    g.moveTo(yx + hx * pulse * 1.4, yy + hy * pulse * 1.4);
    g.lineTo(yx - hx * pulse * 0.8 + px * pulse * 0.8, yy - hy * pulse * 0.8 + py * pulse * 0.8);
    g.lineTo(yx - hx * pulse * 0.4, yy - hy * pulse * 0.4);
    g.lineTo(yx - hx * pulse * 0.8 - px * pulse * 0.8, yy - hy * pulse * 0.8 - py * pulse * 0.8);
    g.closePath(); g.fill();

    // The WAR marker.
    if (d.battle) {
      const [bx, by] = v.proj(d.battle.pos[0], d.battle.pos[1], d.battle.pos[2], s);
      const bp = 8 + Math.sin(now / 220) * 2.4;
      g.strokeStyle = d.battle.active ? 'rgba(255,90,90,.95)' : 'rgba(255,190,90,.8)';
      g.lineWidth = 2;
      g.beginPath(); g.arc(bx, by, bp, 0, Math.PI * 2); g.stroke();
      g.beginPath(); g.moveTo(bx - bp, by - bp); g.lineTo(bx + bp, by + bp); g.stroke();
      g.beginPath(); g.moveTo(bx + bp, by - bp); g.lineTo(bx - bp, by + bp); g.stroke();
      g.lineWidth = 1;
      const mm = `${Math.floor(d.battle.secs / 60)}:${String(Math.floor(d.battle.secs % 60)).padStart(2, '0')}`;
      g.fillStyle = d.battle.active ? '#ff9a9a' : '#ffd28e';
      g.font = '700 12px ui-monospace,monospace';
      g.fillText(d.battle.active ? `⚔ FLEET BATTLE — ${d.battle.name} · ${mm} left` : `⚔ next battle — ${d.battle.name} · in ${mm}`, 14, 20);
    }

    // Screen overlays: scale bar, compass, hover card.
    const barM = niceStep(120 / s, 1);
    const barPx = barM * s;
    g.strokeStyle = '#9fb8d8'; g.fillStyle = '#9fb8d8';
    g.beginPath(); g.moveTo(14, S - 16); g.lineTo(14 + barPx, S - 16); g.moveTo(14, S - 20); g.lineTo(14, S - 12); g.moveTo(14 + barPx, S - 20); g.lineTo(14 + barPx, S - 12); g.stroke();
    g.font = '10px ui-monospace,monospace';
    g.fillText(fmtKm(barM), 14, S - 24);
    const [nx, ny] = v.dir(0, 0, -1);
    const cx = S - 30, cy = 30;
    g.strokeStyle = 'rgba(159,184,216,.5)';
    g.beginPath(); g.arc(cx, cy, 14, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = '#ffd97e';
    g.beginPath(); g.moveTo(cx, cy); g.lineTo(cx + nx * 12, cy + ny * 12); g.stroke();
    g.fillStyle = '#ffd97e';
    g.fillText('N', cx + nx * 20 - 3, cy + ny * 20 + 3);
    this.hover = v.mouse.inside ? this.pick(v.mouse.x, v.mouse.y) : null;
    if (this.canvas) this.canvas.style.cursor = this.hover ? 'pointer' : 'grab';
    if (this.hover) {
      const h = this.hover;
      g.font = '600 11px system-ui';
      const w = Math.max(g.measureText(h.label).width, g.measureText(h.sub).width) + 16;
      const bx = Math.min(S - w - 6, h.sx + 14), by = Math.max(6, h.sy - 36);
      g.fillStyle = 'rgba(8,13,22,.92)'; g.strokeStyle = '#2c4666';
      g.beginPath(); g.roundRect(bx, by, w, 32, 6); g.fill(); g.stroke();
      g.fillStyle = '#e8f0ff'; g.fillText(h.label, bx + 8, by + 13);
      g.fillStyle = '#8fa7c4'; g.font = '10px ui-monospace,monospace'; g.fillText(h.sub, bx + 8, by + 26);
    }
  }
}
